import { observable, action, ObservableMap, computed, toJS } from 'mobx';

// --

export type TValidationRule<A extends {}> = (key: keyof A, value: any, data: A) => void | string;

// Validator opts
export interface IValidatorOpts<A extends {}> {
    rules: Partial<Record<keyof A | 'default', TValidationRule<A>>>;
}

/**
 * Validator
 */
export class Validator<A extends {} = any> {

    // errors map
    public readonly errors: ObservableMap<keyof A, string>;

    /**
     * Class constructor
     * @param opts 
     */
    public constructor(public readonly opts: IValidatorOpts<A>) {
        this.errors = observable.map([], { deep: false });
    }

    /**
     * Set validation fn
     * @param key 
     * @param fn 
     */
    public setRule(key: keyof A | 'default', fn: TValidationRule<A>) {
        this.opts.rules[key] = fn;
    }

    /**
     * Validate
     * @param key 
     * @param value 
     * @param data 
     */
    @action public validate(key: keyof A, value: any, data: A) {
        const fn = this.opts.rules[key] ?? this.opts.rules.default;
        if (!fn) return void 0;

        const errString = fn(key, value, data);

        if (typeof errString === 'undefined') {
            this.errors.delete(key);
            return void 0;
        }
        
        this.errors.set(key, errString);
        return errString;
    }

    /**
     * Validate multiple keys
     * @param key 
     * @param value 
     * @param data 
     */
    @action public mvalidate(patch: Partial<A>, data: A) {
        for (let key in patch) {
            this.validate(key, patch[key], data);
        }
    }

    /**
     * Clear errors
     */
    @action public clear() {
        this.errors.clear();
    }

    /**
     * Has errors
     */
    @computed public get hasErrors() {
        return this.errors.size > 0;
    }

    /**
     * To js
     * @returns 
     */
    public toJS() {
        return  toJS(this.errors);
    }
}